import { fmtMoney } from '../utils/currency';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BookOpen, Plus, X, Trash2, AlertCircle, Search, ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  useToast, useConfirm,
  EmptyState, PageHeader, Button, Input, SkeletonRow,
} from '../components/ui';

const EMPTY_FORM = { name: '', price: '', category: '', description: '' };
const EMPTY_ADDON = { name: '', price: '' };
const PAGE_SIZE = 20;

const Menu = () => {
  const { toast } = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [inventory, setInventory] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [recipe, setRecipe] = useState([]); // [{ inventoryItem, quantity, unit }]
  const [addOns, setAddOns] = useState([]); // [{ name, price }]
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    fetchInventory();
  }, []);

  // Search resets back to page 1; both search and page changes refetch —
  // filtering and paging happen on the server, so the list is never capped
  // by however many items got fetched in one shot.
  useEffect(() => {
    const t = setTimeout(() => fetchMenu(), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [page, search]);

  const fetchMenu = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/menu', {
        params: { page, limit: PAGE_SIZE, search: search || undefined },
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setItems(res.data);
      setTotalCount(parseInt(res.headers['x-total-count'], 10) || res.data.length);
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Could not load the menu');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventory = async () => {
    try {
      const res = await axios.get('/api/inventory?limit=500', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setInventory(res.data);
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Could not load inventory for the recipe editor');
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setRecipe([]);
    setAddOns([]);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '',
      price: item.price ?? '',
      category: item.category || '',
      description: item.description || '',
    });
    const normalized = (item.recipe || []).map(r => ({
      inventoryItem: r.inventoryItem?._id || r.inventoryItem || '',
      quantity: r.quantity ?? '',
      unit: r.unit || '',
    }));
    setRecipe(normalized);
    setAddOns((item.addOns || []).map(a => ({
      name: a.name || '',
      price: a.price ?? '',
      inventoryItem: a.inventoryItem?._id || a.inventoryItem || '',
      consumeQuantity: a.consumeQuantity ?? '',
      consumeUnit: a.consumeUnit || '',
    })));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setRecipe([]);
    setAddOns([]);
  };

  // Add-on row helpers
  const addAddOn = () => setAddOns([...addOns, { ...EMPTY_ADDON }]);
  const updateAddOn = (idx, patch) =>
    setAddOns(addOns.map((a, i) => i === idx ? { ...a, ...patch } : a));
  const removeAddOn = (idx) => setAddOns(addOns.filter((_, i) => i !== idx));

  // Recipe row helpers
  const addIngredient = () => {
    setRecipe([...recipe, { inventoryItem: '', quantity: '', unit: 'pcs' }]);
  };

  const updateIngredient = (idx, patch) => {
    setRecipe(recipe.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const removeIngredient = (idx) => {
    setRecipe(recipe.filter((_, i) => i !== idx));
  };

  // When inventory item is picked, auto-fill its unit if recipe row's unit is empty
  const onIngredientPicked = (idx, inventoryId) => {
    const inv = inventory.find(i => i._id === inventoryId);
    updateIngredient(idx, {
      inventoryItem: inventoryId,
      unit: inv?.unit || recipe[idx].unit || 'pcs',
    });
  };

  // Live cost calculation from current recipe + inventory prices
  const computedCost = recipe.reduce((sum, r) => {
    const inv = inventory.find(i => i._id === r.inventoryItem);
    const qty = Number(r.quantity) || 0;
    return sum + (Number(inv?.price) || 0) * qty;
  }, 0);

  const sellPrice = Number(form.price) || 0;
  const margin = sellPrice > 0 ? ((sellPrice - computedCost) / sellPrice) * 100 : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Filter out incomplete recipe rows
    const cleanRecipe = recipe
      .filter(r => r.inventoryItem && Number(r.quantity) > 0)
      .map(r => ({
        inventoryItem: r.inventoryItem,
        quantity: Number(r.quantity),
        unit: r.unit || 'pcs',
      }));

    // Filter out incomplete add-on rows; include optional inventory mapping if both
    // an inventory item and a positive consume-quantity are set.
    const cleanAddOns = addOns
      .filter(a => a.name && a.name.trim() && Number(a.price) >= 0)
      .map(a => {
        const hasLink = a.inventoryItem && Number(a.consumeQuantity) > 0;
        return {
          name: a.name.trim(),
          price: Number(a.price) || 0,
          inventoryItem: hasLink ? a.inventoryItem : null,
          consumeQuantity: hasLink ? Number(a.consumeQuantity) : 0,
          consumeUnit: hasLink ? (a.consumeUnit || '') : '',
        };
      });

    setSubmitting(true);
    try {
      const payload = { ...form, price: Number(form.price), recipe: cleanRecipe, addOns: cleanAddOns };
      if (editingItem) {
        await axios.put(`/api/menu/${editingItem._id}`, payload, {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });
      } else {
        await axios.post('/api/menu', payload, {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });
      }
      closeModal();
      fetchMenu();
      toast.success(editingItem ? 'Menu item updated' : 'Menu item added');
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error saving menu item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete menu item?',
      message: 'This will permanently remove the item from the menu.',
      variant: 'danger',
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/menu/${id}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      toast.success('Menu item deleted');
      fetchMenu();
    } catch (err) {
      toast.error('Failed to delete menu item');
    }
  };

  return (
    <>
      <PageHeader
        title="Menu"
        subtitle="Define dishes, prices, and the ingredients each one uses."
        action={
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={openAddModal}>
            Add Item
          </Button>
        }
      />

      {/* Items table */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
        <div className="px-5 py-3 border-b border-neutral-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="flex items-center justify-between sm:justify-start gap-3">
            <h2 className="text-sm font-semibold text-neutral-800 whitespace-nowrap">All menu items</h2>
            <span className="text-xs text-neutral-500 whitespace-nowrap">
              {totalCount} item{totalCount === 1 ? '' : 's'}
            </span>
          </div>
          <div className="w-full sm:w-64">
            <Input
              type="text"
              placeholder="Search menu by name or category…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              leftIcon={<Search size={14} />}
              rightIcon={search ? (
                <button type="button" onClick={() => { setSearch(''); setPage(1); }} className="text-neutral-400 hover:text-neutral-700 transition-colors" title="Clear search">
                  <X size={14} />
                </button>
              ) : undefined}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="app-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Price</th>
                <th>Cost</th>
                <th>Margin</th>
                <th>Recipe</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={`s-${i}`} cols={7} />
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan="7">
                    <EmptyState
                      icon={BookOpen}
                      title={search ? 'No items match your search' : 'No menu items yet'}
                      message={search
                        ? 'Try a different name or category.'
                        : 'Click + Add Item to define your first dish.'}
                    />
                  </td>
                </tr>
              )}
              {!loading && items.map(item => {
                const cost = Number(item.costPrice) || 0;
                const price = Number(item.price) || 0;
                const itemMargin = price > 0 ? ((price - cost) / price) * 100 : 0;
                const ingredientCount = item.recipe?.length || 0;
                return (
                  <tr key={item._id}>
                    <td className="font-medium">{item.name}</td>
                    <td className="text-neutral-600">
                      {item.category || <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="tabular-nums">{fmtMoney(price, true)}</td>
                    <td className="tabular-nums text-neutral-600">
                      {cost > 0 ? fmtMoney(cost, true) : <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="tabular-nums">
                      {cost > 0 && price > 0 ? (
                        <span className={itemMargin >= 60 ? 'text-success-600' : itemMargin >= 30 ? 'text-warning-600' : 'text-danger-600'}>
                          {itemMargin.toFixed(0)}%
                        </span>
                      ) : (
                        <span className="text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="text-neutral-600">
                      {ingredientCount === 0 ? (
                        <span className="text-xs text-warning-600 flex items-center gap-1">
                          <AlertCircle size={12} /> No recipe
                        </span>
                      ) : (
                        <span className="text-xs">{ingredientCount} ingredient{ingredientCount === 1 ? '' : 's'}</span>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <Button size="sm" variant="secondary" onClick={() => openEditModal(item)}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(item._id)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalCount > 0 && (
          <div className="px-5 py-3 border-t border-neutral-200 flex items-center justify-between">
            <span className="text-xs text-neutral-500">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<ChevronLeft size={14} />}
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                rightIcon={<ChevronRight size={14} />}
                disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal with recipe editor */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-card-hover w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-neutral-900">
                {editingItem ? 'Edit menu item' : 'Add new menu item'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="text-neutral-400 hover:text-neutral-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-5">
                {/* Basic fields */}
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Name"
                    type="text"
                    placeholder="e.g. Margherita Pizza"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                  <Input
                    label="Selling price"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    required
                  />
                </div>
                <Input
                  label="Category"
                  type="text"
                  placeholder="e.g. Pizza, Drinks"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  required
                />
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-1">Description (optional)</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Brief description that shows on the menu."
                    rows="2"
                    className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500 resize-none"
                  />
                </div>

                {/* Recipe section */}
                <div className="border-t border-neutral-200 pt-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-neutral-900">Recipe</h3>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        Ingredients used per single item. Stock is auto-deducted on every order.
                      </p>
                    </div>
                    {inventory.length > 0 && (
                      <Button type="button" size="sm" variant="secondary" leftIcon={<Plus size={14} />} onClick={addIngredient}>
                        Add ingredient
                      </Button>
                    )}
                  </div>

                  {inventory.length === 0 ? (
                    <div className="p-4 bg-warning-50 border border-warning-200 rounded-md text-sm text-warning-700">
                      No inventory items yet. Add some on the Inventory page first, then come back to define recipes here.
                    </div>
                  ) : recipe.length === 0 ? (
                    <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-md text-sm text-neutral-600 text-center">
                      No ingredients. This item will save without stock tracking.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {recipe.map((row, idx) => {
                        const inv = inventory.find(i => i._id === row.inventoryItem);
                        const lineCost = (Number(inv?.price) || 0) * (Number(row.quantity) || 0);
                        return (
                          <li key={idx} className="flex items-start gap-2 p-2 bg-neutral-50 border border-neutral-200 rounded-md">
                            <div className="flex-1 min-w-0">
                              <select
                                value={row.inventoryItem}
                                onChange={(e) => onIngredientPicked(idx, e.target.value)}
                                className="w-full border border-neutral-300 rounded-md bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                              >
                                <option value="">Select ingredient…</option>
                                {inventory.map(i => (
                                  <option key={i._id} value={i._id}>
                                    {i.name} ({i.unit || 'pcs'})
                                  </option>
                                ))}
                              </select>
                              {inv && (
                                <p className="text-[11px] text-neutral-500 mt-1">
                                  In stock: <span className="tabular-nums font-medium">{inv.quantity}</span> {inv.unit} · {fmtMoney(Number(inv.price) || 0)} per {inv.unit}
                                </p>
                              )}
                            </div>
                            <div className="w-24 flex-shrink-0">
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Qty"
                                value={row.quantity}
                                onChange={(e) => updateIngredient(idx, { quantity: e.target.value })}
                                className="w-full border border-neutral-300 rounded-md bg-white px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                              />
                              {lineCost > 0 && (
                                <p className="text-[11px] text-neutral-500 mt-1 text-right tabular-nums">
                                  {fmtMoney(lineCost, true)}
                                </p>
                              )}
                            </div>
                            <div className="w-20 flex-shrink-0">
                              <input
                                type="text"
                                placeholder="unit"
                                value={row.unit}
                                onChange={(e) => updateIngredient(idx, { unit: e.target.value })}
                                className="w-full border border-neutral-300 rounded-md bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeIngredient(idx)}
                              className="w-8 h-8 rounded-md flex items-center justify-center text-neutral-400 hover:text-danger-600 hover:bg-danger-50 transition-colors"
                              title="Remove ingredient"
                            >
                              <Trash2 size={14} />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {/* Cost / margin preview */}
                  {(computedCost > 0 || sellPrice > 0) && (
                    <div className="mt-4 p-3 bg-primary-50 border border-primary-100 rounded-md flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                      <div>
                        <span className="text-neutral-600">Ingredient cost: </span>
                        <span className="font-semibold tabular-nums">{fmtMoney(computedCost, true)}</span>
                      </div>
                      {sellPrice > 0 && (
                        <>
                          <div>
                            <span className="text-neutral-600">Selling price: </span>
                            <span className="font-semibold tabular-nums">{fmtMoney(sellPrice, true)}</span>
                          </div>
                          <div>
                            <span className="text-neutral-600">Margin: </span>
                            <span className={`font-semibold tabular-nums ${margin >= 60 ? 'text-success-700' : margin >= 30 ? 'text-warning-700' : 'text-danger-700'}`}>
                              {margin.toFixed(0)}%
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Add-ons section */}
                <div className="border-t border-neutral-200 pt-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-neutral-900">Add-ons</h3>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        Optional paid extras the cashier can attach at order time (e.g., extra cheese).
                      </p>
                    </div>
                    <Button type="button" size="sm" variant="secondary" leftIcon={<Plus size={14} />} onClick={addAddOn}>
                      Add add-on
                    </Button>
                  </div>

                  {addOns.length === 0 ? (
                    <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-md text-sm text-neutral-600 text-center">
                      No add-ons. This item will save without optional extras.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {addOns.map((row, idx) => {
                        const inv = inventory.find(i => i._id === row.inventoryItem);
                        return (
                          <li key={idx} className="p-2 bg-neutral-50 border border-neutral-200 rounded-md space-y-2">
                            {/* Row 1: name + price + remove */}
                            <div className="flex items-start gap-2">
                              <div className="flex-1 min-w-0">
                                <input
                                  type="text"
                                  placeholder="e.g. Extra cheese"
                                  value={row.name}
                                  onChange={(e) => updateAddOn(idx, { name: e.target.value })}
                                  className="w-full border border-neutral-300 rounded-md bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                                />
                              </div>
                              <div className="w-28 flex-shrink-0">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="Price"
                                  value={row.price}
                                  onChange={(e) => updateAddOn(idx, { price: e.target.value })}
                                  className="w-full border border-neutral-300 rounded-md bg-white px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeAddOn(idx)}
                                className="w-8 h-8 rounded-md flex items-center justify-center text-neutral-400 hover:text-danger-600 hover:bg-danger-50 transition-colors"
                                title="Remove add-on"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            {/* Row 2: optional inventory link */}
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] text-neutral-500 uppercase tracking-wider w-16 flex-shrink-0">Deducts</span>
                              <div className="flex-1 min-w-0">
                                <select
                                  value={row.inventoryItem || ''}
                                  onChange={(e) => {
                                    const newInvId = e.target.value;
                                    const picked = inventory.find(i => i._id === newInvId);
                                    updateAddOn(idx, {
                                      inventoryItem: newInvId,
                                      consumeUnit: picked?.unit || row.consumeUnit || '',
                                    });
                                  }}
                                  className="w-full border border-neutral-300 rounded-md bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                                >
                                  <option value="">Nothing (price-only)</option>
                                  {inventory.map(i => (
                                    <option key={i._id} value={i._id}>{i.name} ({i.unit || 'pcs'})</option>
                                  ))}
                                </select>
                              </div>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Qty"
                                disabled={!row.inventoryItem}
                                value={row.consumeQuantity || ''}
                                onChange={(e) => updateAddOn(idx, { consumeQuantity: e.target.value })}
                                className="w-20 border border-neutral-300 rounded-md bg-white px-2 py-1.5 text-xs tabular-nums focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500 disabled:bg-neutral-100 disabled:text-neutral-400"
                              />
                              <input
                                type="text"
                                placeholder="unit"
                                disabled={!row.inventoryItem}
                                value={row.consumeUnit || ''}
                                onChange={(e) => updateAddOn(idx, { consumeUnit: e.target.value })}
                                className="w-16 border border-neutral-300 rounded-md bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500 disabled:bg-neutral-100 disabled:text-neutral-400"
                              />
                            </div>
                            {inv && row.consumeQuantity && (
                              <p className="text-[11px] text-neutral-500 pl-[64px]">
                                In stock: <span className="tabular-nums">{inv.quantity}</span> {inv.unit}
                              </p>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-lg flex gap-2 sticky bottom-0">
                <Button type="button" variant="secondary" fullWidth onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" fullWidth loading={submitting}>
                  {editingItem ? 'Update item' : 'Add item'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Menu;
